import { useEffect, useRef } from "react";

import {
  ChatGraphQLError,
  GET_CHAT_ROOM_QUERY,
  GetChatRoomInput,
  GetChatRoomResponse,
  LOG_IN_MESSENGER_MUTATION,
  PostMessengerLoginResponse,
} from "@/utils/GraphQLClient/messenger";
import { isMessengerEnabled } from "@/utils/feature-flags";

import * as Sentry from "@sentry/react";
import { UseQueryExecute, useMutation, useQuery } from "urql";

import { ChatConfiguration } from "../utils/GraphQLClient/messenger";

const isChatGraphQLError = (e: unknown): e is ChatGraphQLError =>
  !!e && typeof e === "object" && "code" in e && "message" in e;

/**
 * Log into messenger feature
 * @returns Client-side JWT token and configuration
 */
export const useMessengerLogin = (): {
  token?: string;
  config?: ChatConfiguration;
  loading: boolean;
  error?: ChatGraphQLError;
} => {
  const messengerEnabled = isMessengerEnabled();
  const [{ data, fetching, error }, messengerLogin] =
    useMutation<PostMessengerLoginResponse>(LOG_IN_MESSENGER_MUTATION);
  const hasLoggedIn = useRef(false);

  useEffect(() => {
    if (hasLoggedIn.current || !messengerEnabled) {
      return;
    }
    hasLoggedIn.current = true;

    const handleLogin = async () => {
      const result = await messengerLogin();
      const error = result.error?.graphQLErrors.at(0)?.originalError;
      if (!error) {
        return;
      }
      const mutationErr = isChatGraphQLError(error) ? (error as unknown as ChatGraphQLError) : undefined;
      if (mutationErr?.code !== "400" && mutationErr?.code !== "401" && mutationErr?.code !== "403") {
        Sentry.captureException("Messenger: logInToMessenger mutation error", {
          level: "error",
          tags: { component: "usePatientMessenger", feature: "messenger", operation: "LOG_IN_TO_MESSENGER" },
          extra: { error: JSON.stringify(error) },
        });
      }
    };

    handleLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The mutation is triggered in useEffect (after the first render), so
  // `fetching` is false before it fires. Treat the hook as loading until
  // either data or an error has been received.
  const isLoading = fetching || (!data && !error);

  if (!messengerEnabled) {
    return { token: undefined, config: undefined, loading: false };
  }

  if (error) {
    const err = error?.graphQLErrors.at(0)?.originalError;
    const chatError = isChatGraphQLError(err) ? (err as unknown as ChatGraphQLError) : undefined;
    return {
      token: undefined,
      config: undefined,
      loading: false,
      ...(chatError && {
        error: chatError,
      }),
    };
  }

  const chatSession = data?.initiateMessengerAuth;
  const response = {
    token: chatSession?.token,
    loading: isLoading,
    config: chatSession?.config,
  };

  return response;
};

/**
 * Retrieves a patient's chat room
 * @returns Chat room JID, if found.
 */
export const usePatientChatRoom = (
  fhir_patient_id?: string
): { loading: boolean; room_jid?: string; reexecute_query?: UseQueryExecute; error?: ChatGraphQLError } => {
  const messengerEnabled = isMessengerEnabled();
  const [{ data: chatRoomData, fetching, error }, reexecute_query] = useQuery<
    GetChatRoomResponse,
    { input: GetChatRoomInput }
  >({
    query: GET_CHAT_ROOM_QUERY,
    variables: {
      input: {
        fhir_patient_id: fhir_patient_id ?? "",
      },
    },
    pause: !fhir_patient_id || !messengerEnabled,
    requestPolicy: "cache-and-network",
  });

  if (!messengerEnabled) {
    return {
      room_jid: undefined,
      loading: false,
    };
  }
  if (error) {
    const err = error?.graphQLErrors.at(0)?.originalError;
    const chatRoomError = isChatGraphQLError(err) ? (err as unknown as ChatGraphQLError) : undefined;

    if (err && chatRoomError?.code !== "400" && chatRoomError?.code !== "401" && chatRoomError?.code !== "403") {
      Sentry.captureException("Messenger: getChatRoom query error", {
        level: "error",
        tags: { component: "usePatientMessenger", feature: "messenger", operation: "GET_CHAT_ROOM" },
        extra: { error: JSON.stringify(error.graphQLErrors) },
      });
    }

    return {
      room_jid: undefined,
      loading: fetching,
      ...(chatRoomError && {
        error: chatRoomError,
      }),
    };
  }
  return {
    room_jid: chatRoomData?.getChatRoom?.name,
    loading: fetching,
    reexecute_query,
  };
};
